/**
 * useAutoIa
 *
 * Owns the two orchestrator pipelines that chain multiple AI steps together:
 *
 * 1. **Auto IA** (manual, triggered by a button) — runs:
 *    analyze → compare & optimize (parallel) → generate tailored CV
 *    → new version → cover letter. Uses cached step results when available.
 *    Falls back to Model 2 on Model 1 timeouts via `fetchWithFallback`.
 *
 * 2. **Extension Analyse IA** (triggered after the Chrome extension posts a
 *    job offer via postMessage) — runs:
 *    parse job → analyze → compare → optimize. Auto-populates the job target
 *    state along the way. Fires when `runAnalyzeIaAfterJob` flips to true and
 *    a raw job text of at least 80 chars is available.
 *
 * Also owns:
 * - `availableModels` (fetched on mount) — used both by `fetchWithFallback`
 *   and by the ATS sidebar info bubble.
 * - `pipelineProgress` — shared progress state displayed by
 *   `PipelineProgressModal` (both kinds of pipelines push into it).
 *
 * The hook deliberately takes the cross-cutting setters from the other hooks
 * as params (useAiAnalyses, useCvState, useJobTarget, useCoverLetter)
 * instead of trying to collapse them — the orchestrator needs to mutate state
 * owned by 5 distinct concerns and this keeps the coupling explicit.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import type { CVData, ParsedJobOffer } from '../types';
import type {
  AiAnalyzeStructured,
  AiCompareStructured,
  AiAtsOptimizeStructured,
  AtsSectionConfig,
  AdaptationLevelMap,
} from '../lib/ai-types';
import { parseJsonResponse, postJsonWithTimeout } from '../lib/fetch-utils';
import {
  createCvFingerprint,
  createCvJobFingerprint,
  getCachedStep,
  setCachedStep,
} from '../lib/client-cache';
import { cloneCvData, makeTemplateId } from '../lib/cv-utils';
import { parsedOfferToAnalysisText } from '../lib/job-url-scraper';
import type { TemplateMetaItem } from './useCvState';
import { toast } from '../lib/toast';

/** Shape of the pipeline progress popup state. */
export type PipelineProgress = {
  kind: 'autoIa' | 'extensionAnalyze';
  step: number;
  labels: string[];
  successMessage?: string;
  fallbackMessage?: string;
};

export type AvailableModels = {
  model1: string | null;
  model2: string | null;
};

const AUTO_IA_LABELS = [
  'Analyse CV IA',
  'Comparaison',
  'Optimisation ATS',
  'Génération CV',
  'Nouvelle version',
  'Lettre de motivation',
];

const EXTENSION_PIPELINE_LABELS = [
  'Parsing annonce',
  'Analyse auto CV',
  'Comparaison',
  'Optimisation ATS',
];

const isTimeoutError = (errMsg: string): boolean => {
  const lower = errMsg.toLowerCase();
  return lower.includes('timeout') || lower.includes('délai') || lower.includes('abort');
};

export interface UseAutoIaParams {
  // CV / job context
  activeTemplate: string;
  cvData: CVData;
  getResumeText: (cv: CVData) => string;
  effectiveJobDescription: string;
  rawJobTextForAi: string;
  parsedJobOffer: ParsedJobOffer | null;
  jobTitle: string;
  templateMeta: TemplateMetaItem[];
  locale: 'fr' | 'en';

  // Generation config
  atsSectionConfig: AtsSectionConfig;
  atsAdaptationLevel: AdaptationLevelMap;

  // Cover-letter config
  coverLetterLength: 'short' | 'standard' | 'long';
  coverLetterFidelity: 'fidele' | 'modere' | 'creatif';
  coverLetterTone: string;

  // Cross-hook setters (explicit coupling).
  setAiAnalysisStructured: Dispatch<SetStateAction<AiAnalyzeStructured | null>>;
  setAiCompareStructured: Dispatch<SetStateAction<AiCompareStructured | null>>;
  setAiOptimizeStructured: Dispatch<SetStateAction<AiAtsOptimizeStructured | null>>;
  setAllTemplatesData: Dispatch<SetStateAction<Record<string, CVData>>>;
  setTemplateMeta: Dispatch<SetStateAction<TemplateMetaItem[]>>;
  setActiveTemplate: Dispatch<SetStateAction<string>>;
  setParsedJobOffer: (offer: ParsedJobOffer | null) => void;
  setJobDescription: (v: string) => void;
  setGeneratedCoverLetterByVersionId: Dispatch<SetStateAction<Record<string, string>>>;

  /** Called after a new version is created by Auto IA, with the original and adapted CV data */
  onVersionCreated?: (before: CVData, after: CVData) => void;
}

export interface UseAutoIaReturn {
  isAutoIaRunning: boolean;
  pipelineProgress: PipelineProgress | null;
  setPipelineProgress: Dispatch<SetStateAction<PipelineProgress | null>>;

  /** Flag set to true by the Chrome extension message listener; the hook
   *  consumes it to trigger the extension-analyze pipeline. */
  runAnalyzeIaAfterJob: boolean;
  setRunAnalyzeIaAfterJob: Dispatch<SetStateAction<boolean>>;

  availableModels: AvailableModels;

  runAutoIa: () => Promise<void>;
}

export function useAutoIa(params: UseAutoIaParams): UseAutoIaReturn {
  const [isAutoIaRunning, setIsAutoIaRunning] = useState(false);
  const [runAnalyzeIaAfterJob, setRunAnalyzeIaAfterJob] = useState(false);
  const [pipelineProgress, setPipelineProgress] = useState<PipelineProgress | null>(null);
  const [availableModels, setAvailableModels] = useState<AvailableModels>({
    model1: null,
    model2: null,
  });

  // Keep params in a ref so long-running async pipelines always see the
  // latest values without having to re-create the callback on every render.
  const paramsRef = useRef(params);
  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  // ---- fetch /api/ai?route=models on mount ----
  useEffect(() => {
    fetch('/api/ai', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ route: 'models' }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.model1 || data.model2) {
          setAvailableModels(data);
        }
      })
      .catch(() => {
        /* ignore — the feature degrades gracefully */
      });
  }, []);

  /**
   * Helper: POST to /api/ai with client-side timeout and HTML-aware error
   * parsing. Returns a compatible shape with `ok` + a cached `json()` so
   * existing callers don't need to change.
   *
   * Historical note: this used to try to auto-retry with `forceModel: 'model2'`
   * on timeouts, but the server never actually read that parameter. The real
   * OpenRouter ↔ MiniMax fallback happens inside ai-provider.ts. This helper
   * now just wraps the call with a client-side timeout and a HTML-safe
   * error surface.
   */
  const fetchWithFallback = useCallback(
    async (
      body: Record<string, unknown>,
      setFallback: (msg: string) => void,
    ): Promise<{ ok: boolean; json: () => Promise<unknown> }> => {
      try {
        const { res, json } = await postJsonWithTimeout<unknown>('/api/ai', body);
        // Cache the parsed JSON so callers can re-read it via json() without
        // hitting the network twice.
        return { ok: res.ok, json: async () => json };
      } catch (e) {
        // Surface a friendly error via the fallback-message channel (used
        // by the progress modal) instead of crashing the whole pipeline.
        const msg = e instanceof Error ? e.message : 'Erreur réseau';
        if (isTimeoutError(msg)) {
          setFallback('Délai dépassé côté client — réessayez');
        }
        return {
          ok: false,
          json: async () => ({ error: msg }),
        };
      }
    },
    [],
  );

  // ---------- Extension analyze pipeline (triggered by runAnalyzeIaAfterJob) ----------
  useEffect(() => {
    if (!runAnalyzeIaAfterJob) return;
    const rawJobText = paramsRef.current.rawJobTextForAi;
    if (!rawJobText || rawJobText.length < 80) return;

    setRunAnalyzeIaAfterJob(false);
    setPipelineProgress({
      kind: 'extensionAnalyze',
      step: 0,
      labels: EXTENSION_PIPELINE_LABELS,
    });

    (async () => {
      try {
        const {
          cvData,
          getResumeText,
          setParsedJobOffer,
          setJobDescription,
          setAiAnalysisStructured,
          setAiCompareStructured,
          setAiOptimizeStructured,
        } = paramsRef.current;

        setPipelineProgress((p) => (p?.kind === 'extensionAnalyze' ? { ...p, step: 0 } : p));
        const parseRes = await fetch('/api/ai', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ route: 'parse', jobDescription: rawJobText }),
        });
        const parseJson = await parseJsonResponse<{ error?: string; data?: unknown }>(parseRes);
        if (!parseRes.ok || !parseJson.data || typeof parseJson.data !== 'object') {
          setPipelineProgress(null);
          return;
        }
        const parsed = parseJson.data as ParsedJobOffer;
        setParsedJobOffer(parsed);
        const jd =
          parsed.description && parsed.description.trim().length >= 50
            ? parsed.description.trim()
            : parsedOfferToAnalysisText(parsed);
        setJobDescription(jd);

        const resumeText = getResumeText(cvData);

        setPipelineProgress((p) => (p?.kind === 'extensionAnalyze' ? { ...p, step: 1 } : p));
        const analyzeRes = await fetch('/api/ai', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ route: 'analyze', resumeText, jobDescription: jd }),
        });
        const analyzeJson = await parseJsonResponse<{ error?: string; structured?: unknown }>(
          analyzeRes,
        );
        if (analyzeRes.ok && analyzeJson.structured) {
          setAiAnalysisStructured(analyzeJson.structured as AiAnalyzeStructured);
        }

        setPipelineProgress((p) => (p?.kind === 'extensionAnalyze' ? { ...p, step: 2 } : p));
        const compareRes = await fetch('/api/ai', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ route: 'compare', resumeText, jobDescription: jd }),
        });
        const compareJson = await parseJsonResponse<{ error?: string; structured?: unknown }>(
          compareRes,
        );
        if (compareRes.ok && compareJson.structured) {
          setAiCompareStructured(compareJson.structured as AiCompareStructured);
        }

        setPipelineProgress((p) => (p?.kind === 'extensionAnalyze' ? { ...p, step: 3 } : p));
        const optimizeRes = await fetch('/api/ai', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            route: 'ats-optimize',
            action: 'optimize',
            resumeText,
            jobDescription: jd,
          }),
        });
        const optimizeJson = await parseJsonResponse<{
          error?: string;
          structured?: unknown;
        }>(optimizeRes);
        if (optimizeRes.ok && optimizeJson.structured) {
          setAiOptimizeStructured(optimizeJson.structured as AiAtsOptimizeStructured);
        }

        setPipelineProgress((p) =>
          p?.kind === 'extensionAnalyze'
            ? { ...p, successMessage: 'Analyse IA terminée' }
            : p,
        );
        setTimeout(() => setPipelineProgress(null), 2500);
      } catch (e) {
        console.error('[Pipeline Analyse IA]', e);
        setPipelineProgress(null);
      }
    })();
  }, [runAnalyzeIaAfterJob]);

  // ---------- Auto IA (manual button) ----------
  const runAutoIa = useCallback(async () => {
    const {
      activeTemplate,
      cvData,
      getResumeText,
      effectiveJobDescription,
      parsedJobOffer,
      jobTitle,
      templateMeta,
      locale,
      atsSectionConfig,
      atsAdaptationLevel,
      coverLetterLength,
      coverLetterFidelity,
      coverLetterTone,
      setAiAnalysisStructured,
      setAiCompareStructured,
      setAiOptimizeStructured,
      setAllTemplatesData,
      setTemplateMeta,
      setActiveTemplate,
      setGeneratedCoverLetterByVersionId,
    } = paramsRef.current;

    const effectiveJd = effectiveJobDescription?.trim();
    if (!effectiveJd || effectiveJd.length < 80) {
      toast.error('Saisissez ou récupérez une annonce (au moins 80 caractères) avant de lancer Auto IA.');
      return;
    }

    setIsAutoIaRunning(true);
    setPipelineProgress({ kind: 'autoIa', step: 0, labels: AUTO_IA_LABELS });
    try {
      const resumeText = getResumeText(cvData);
      const versionNameBase = parsedJobOffer?.titre || jobTitle?.trim() || cvData.personalInfo.title?.trim() || 'Nouveau CV';
      const fingerprint = createCvJobFingerprint(activeTemplate, resumeText, effectiveJd);
      const analyzeFingerprint = createCvFingerprint(activeTemplate, resumeText);

      let currentStepName = 'Init';

      const updateFallback = (msg: string) =>
        setPipelineProgress((p) => (p ? { ...p, fallbackMessage: msg } : p));

      // Step 0: analyze (cached when possible).
      currentStepName = 'Analyse CV IA';
      setPipelineProgress((p) =>
        p?.kind === 'autoIa' ? { ...p, step: 0, fallbackMessage: undefined } : p,
      );
      let analysisStructured: AiAnalyzeStructured | null = null;
      const cachedAnalyze = getCachedStep<AiAnalyzeStructured>(
        `cv_ai_analysis_structured_${activeTemplate}`,
        `cv_ai_analyze_fp_${activeTemplate}`,
        analyzeFingerprint,
      );
      if (cachedAnalyze) {
        analysisStructured = cachedAnalyze.data;
      } else {
        const { ok, json } = await fetchWithFallback(
          { route: 'analyze', resumeText, jobDescription: effectiveJd },
          updateFallback,
        );
        const analyzeJson = (await json()) as { error?: string; structured?: unknown };
        if (!ok) throw new Error(analyzeJson.error || `Échec à l'étape "${currentStepName}"`);
        analysisStructured = (analyzeJson.structured as AiAnalyzeStructured | null) ?? null;
        if (analysisStructured) {
          setCachedStep(
            `cv_ai_analysis_structured_${activeTemplate}`,
            `cv_ai_analyze_fp_${activeTemplate}`,
            analyzeFingerprint,
            analysisStructured,
          );
        }
      }
      setAiAnalysisStructured(analysisStructured ?? null);

      // Step 1: compare & optimize (run in parallel, cached when possible).
      currentStepName = 'Comparaison & Optimisation';
      setPipelineProgress((p) =>
        p?.kind === 'autoIa' ? { ...p, step: 1, fallbackMessage: undefined } : p,
      );
      let compareStructured: AiCompareStructured | null = null;
      let optimizeStructured: AiAtsOptimizeStructured | null = null;
      const cachedCompare = getCachedStep<AiCompareStructured>(
        `cv_ai_compare_structured_${activeTemplate}`,
        `cv_ai_compare_fp_${activeTemplate}`,
        fingerprint,
      );
      const cachedOptimize = getCachedStep<AiAtsOptimizeStructured>(
        `cv_ai_optimize_structured_${activeTemplate}`,
        `cv_ai_optimize_fp_${activeTemplate}`,
        fingerprint,
      );
      if (cachedCompare) compareStructured = cachedCompare.data;
      if (cachedOptimize) optimizeStructured = cachedOptimize.data;

      if (!cachedCompare || !cachedOptimize) {
        const comparePromise = cachedCompare
          ? Promise.resolve({
              ok: true,
              json: () =>
                Promise.resolve({}) as Promise<{ error?: string; structured?: unknown }>,
            })
          : fetchWithFallback(
              { route: 'compare', resumeText, jobDescription: effectiveJd },
              updateFallback,
            );
        const optimizePromise = cachedOptimize
          ? Promise.resolve({
              ok: true,
              json: () =>
                Promise.resolve({}) as Promise<{ error?: string; structured?: unknown }>,
            })
          : fetchWithFallback(
              {
                route: 'ats-optimize',
                action: 'optimize',
                resumeText,
                jobDescription: effectiveJd,
              },
              updateFallback,
            );
        const [compareRes, optimizeRes] = await Promise.all([comparePromise, optimizePromise]);

        const errors: string[] = [];
        if (!cachedCompare) {
          const compareJson = (await compareRes.json()) as {
            error?: string;
            structured?: unknown;
          };
          if (!compareRes.ok)
            errors.push(compareJson.error || `Échec à l'étape "Comparaison"`);
          else if (compareJson.structured) {
            compareStructured = compareJson.structured as AiCompareStructured;
            setCachedStep(
              `cv_ai_compare_structured_${activeTemplate}`,
              `cv_ai_compare_fp_${activeTemplate}`,
              fingerprint,
              compareStructured,
            );
          }
        }
        if (!cachedOptimize) {
          const optimizeJson = (await optimizeRes.json()) as {
            error?: string;
            structured?: unknown;
          };
          if (!optimizeRes.ok)
            errors.push(optimizeJson.error || `Échec à l'étape "Optimisation ATS"`);
          else if (optimizeJson.structured) {
            optimizeStructured = optimizeJson.structured as AiAtsOptimizeStructured;
            setCachedStep(
              `cv_ai_optimize_structured_${activeTemplate}`,
              `cv_ai_optimize_fp_${activeTemplate}`,
              fingerprint,
              optimizeStructured,
            );
          }
        }
        if (errors.length > 0) throw new Error(errors.join('; '));
      }
      setAiCompareStructured(compareStructured ?? null);
      setAiOptimizeStructured(optimizeStructured ?? null);

      // Step 2: generate tailored CV.
      currentStepName = 'Génération CV';
      setPipelineProgress((p) =>
        p?.kind === 'autoIa' ? { ...p, step: 3, fallbackMessage: undefined } : p,
      );
      const genRes = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          route: 'ats-optimize',
          action: 'generate',
          resumeText,
          jobDescription: effectiveJd,
          cvData,
          sectionConfig: atsSectionConfig,
          adaptationLevel: atsAdaptationLevel,
        }),
      });
      const genJson = await parseJsonResponse<{ error?: string; data?: CVData }>(genRes);
      if (!genRes.ok) throw new Error(genJson.error || `Échec à l'étape "${currentStepName}"`);
      const adaptedCv = genJson.data;
      if (!adaptedCv) throw new Error('Aucun CV généré');

      const adapted = cloneCvData(adaptedCv);
      if (cvData.personalInfo.avatar) {
        adapted.personalInfo = {
          ...adapted.personalInfo,
          avatar: cvData.personalInfo.avatar,
        };
      }

      // Step 3: create new version.
      currentStepName = 'Nouvelle version';
      setPipelineProgress((p) => (p?.kind === 'autoIa' ? { ...p, step: 4 } : p));
      const versionName = versionNameBase.slice(0, 60);
      const existingIds = templateMeta.map((tpl) => tpl.id);
      const newId = makeTemplateId(versionName, existingIds);
      setAllTemplatesData((prev) => ({ ...prev, [newId]: adapted }));
      setTemplateMeta((prev) => [...prev, { id: newId, name: versionName }]);
      setActiveTemplate(newId);
      paramsRef.current.onVersionCreated?.(cvData, adapted);

      // Step 4: cover letter (soft-fails).
      currentStepName = 'Lettre de motivation';
      setPipelineProgress((p) => (p?.kind === 'autoIa' ? { ...p, step: 5 } : p));
      const targetLang = locale;
      const letterRes = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          route: 'ats-optimize',
          action: 'coverLetter',
          resumeText: getResumeText(adapted),
          jobDescription: effectiveJd,
          cvData: adapted,
          targetLang,
          analysisStructured: analysisStructured ?? undefined,
          compareStructured: compareStructured ?? undefined,
          optimizeStructured: optimizeStructured ?? undefined,
          length: coverLetterLength,
          fidelityLevel: coverLetterFidelity,
          tone: coverLetterTone.trim() || 'professionnel',
        }),
      });
      const letterJson = await parseJsonResponse<{ error?: string; letter?: string }>(letterRes);
      if (letterRes.ok && letterJson.letter) {
        setGeneratedCoverLetterByVersionId((prev) => ({
          ...prev,
          [newId]: letterJson.letter!,
        }));
      }

      setPipelineProgress((p) =>
        p?.kind === 'autoIa' ? { ...p, successMessage: `CV ${versionName} créé` } : p,
      );
      setTimeout(() => setPipelineProgress(null), 3500);
    } catch (e) {
      console.error('[Auto IA]', e);
      const errorMsg = e instanceof Error ? e.message : 'Erreur Auto IA';
      setPipelineProgress((p) => (p ? { ...p, successMessage: `Erreur: ${errorMsg}` } : null));
      setTimeout(() => setPipelineProgress(null), 4000);
    } finally {
      setIsAutoIaRunning(false);
    }
  }, [fetchWithFallback]);

  return {
    isAutoIaRunning,
    pipelineProgress,
    setPipelineProgress,
    runAnalyzeIaAfterJob,
    setRunAnalyzeIaAfterJob,
    availableModels,
    runAutoIa,
  };
}
